import React, { useEffect, useState } from "react";
import {
  Upload,
  Button,
  Select,
  Form,
  Typography,
  Row,
  Col,
  message,
} from "antd";
import {
  UploadOutlined,
  InboxOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

const { Dragger } = Upload;
const { Title } = Typography;

const allowedTypes = [".jpg", ".png", ".jpeg", ".pdf"];
const maxSizeMB = 10;

const CategorizedUpload = ({
  data,
  fileList,
  setFileList,
  removeFile,
  form,
  categoryRules,
  locked,
}) => {
  const handleBeforeUpload = (file) => {
    const isAllowed = allowedTypes.some((type) => file.name.endsWith(type));
    const isSizeOk = file.size / 1024 / 1024 < maxSizeMB;

    if (!isAllowed) {
      message.error(`${file.name} is not an allowed file type`);
      return Upload.LIST_IGNORE;
    }
    if (!isSizeOk) {
      message.error(`${file.name} exceeds the 10MB limit`);
      return Upload.LIST_IGNORE;
    }
    return false; // Prevent auto upload
  };

  useEffect(() => {
    const formatted = data?.map((file) => ({
      uid: file.uid,
      name: file.name,
      status: "done",
      url: file.url,
      category: file.category || null,
      fromBackend: true,
      ...file,
    }));
    if (typeof setFileList == "function") {
      setFileList(formatted);
    }
  }, [data]);

  async function deleteFile(actions, file) {
    await removeFile(file);
    actions.remove();
  }

  function itemToRender(originNode, file, fileList, actions) {
    return (
      <div className="row align-items-center py-2 border-bottom gx-3">
        <div className="col">
          <a
            href={file?.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary text-break"
          >
            {file?.name}
            {/* ({(file?.size / 1024 / 1024).toFixed(1)} MB) */}
          </a>
        </div>
        <div className="col-auto">
          <Select
            disabled={parseInt(locked) ? true : false}
            placeholder="Choose Category"
            style={{ width: 180 }}
            value={file.category}
            onChange={(value) => {
              const updated = fileList.map((f) =>
                f.uid === file.uid ? { ...f, category: value } : f,
              );
              setFileList(updated);
            }}
            options={[
              { label: "Birth Certificate", value: "birthCert" },
              { label: "Report Card", value: "reportCard" },
              { label: "Passport Photo", value: "passportPhoto" },
              { label: "Parent/Guardian ID", value: "parentIDCard" },
            ]}
          />
        </div>
        <div className="col-auto">
          {parseInt(locked) ? (
            ""
          ) : (
            <Button
              icon={<DeleteOutlined />}
              onClick={(e) => deleteFile(actions, file)}
              type="link"
              danger
            />
          )}
        </div>
      </div>
    );
  }

  async function validate(_, val) {
    const value = fileList;

    // If all files are from backend, skip validation
    const allFromBackend =
      value?.length > 0 && value?.every((file) => file.fromBackend);
    if (allFromBackend) {
      return Promise.resolve(); // Skip validation
    }
    // Check for missing required categories
    const uploadedCategories = value?.map((f) => f.category);
    for (const [key, rule] of Object?.entries(categoryRules)) {
      if (rule.required && !uploadedCategories?.includes(key)) {
        throw new Error(`Missing required file: ${categoryRules?.[key]?.name}`);
      }
    }

    // Check for missing category selection
    const missingCategory = value.some((file) => !file.category);
    if (missingCategory) {
      throw new Error("Each file must have a selected category.");
    }

    // Check for duplicate categories
    const hasDuplicates =
      new Set(uploadedCategories).size !== uploadedCategories.length;
    if (hasDuplicates) {
      throw new Error("Each category must be unique.");
    }

    // MIME type validation
    for (const file of value) {
      const mime = file.type || file.originFileObj?.type;
      const rule = categoryRules[file.category];
      if (rule && !rule?.mimeTypes?.includes(mime)) {
        throw new Error(
          `Invalid file type for ${file.category}. Allowed: ${rule.mimeTypes.join(", ")}`,
        );
      }
    }

    return Promise.resolve();
  }

  return (
    <div>
      <Title level={3}>Upload Files</Title>
      <Form.Item
        name="uploads"
        rules={[
          {
            validator: validate,
          },
        ]}
      >
        <Dragger
          maxCount={4}
          fileList={fileList}
          onChange={({ fileList }) => {
            typeof setFileList == "function" && setFileList(fileList);
            form.setFieldsValue({ uploads: fileList }); // sync with Form
          }}
          itemRender={itemToRender}
          accept=".jpg,.jpeg,.png,.pdf"
          beforeUpload={handleBeforeUpload}
          multiple
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag files to this area to upload
            <br />
            <label className="text-danger">
              Files to Upload: Birth Certificate, Report Card, Passport Photo,
              Parent/Gaurdian ID Card
            </label>
          </p>
          <p className="ant-upload-hint">
            Supported formats: .jpg, .jpeg, .png, .pdf — Max size: 10MB
          </p>
          <p className="ant-upload-hint">
            Report Card is not required for Baby Class
          </p>
        </Dragger>
      </Form.Item>
    </div>
  );
};

export default CategorizedUpload;
